/**
 * @fileoverview Account Creation Component
 *
 * This file implements the first step in the user onboarding process where users
 * create their account by providing basic information like name and email.
 * It includes form validation, privacy policy acceptance, and communicates
 * with the global state to persist user information.
 *
 * Key features:
 * - Form validation for required fields
 * - Privacy policy acceptance
 * - Field disabling for pre-filled information
 * - Error message handling
 * - Integration with global state management
 */

import {
  CheckBox,
  TextInput,
  LabelPosition,
  LabelAlign,
  SelectorVariant,
  Button,
  ButtonVariant,
  TextInputWidth,
  TextInputSize,
  LabelSize,
  Link,
  IconName,
  ButtonIconAlign,
  TextInputIconAlign,
  Icon,
  IconSize,
} from '@eightfold.ai/octuple';
import React, { useEffect, useState, useContext } from 'react';
import { actionButtonStyle } from '../../commons/commonStyles';
import { useNavigate } from 'react-router';
import { validateFields, validationOptions } from '../../ValidationSchema';
import { globalContext } from '../../store';
import { updateDemographicData, ShowStepper } from '../../commons/commonFunctions';
import Api from '../../Api';

/**
 * Account Component
 *
 * Renders the first step of the onboarding process where users create their account
 * by entering their first name, last name, email, and accepting the privacy policy.
 *
 * @param {Object} props - Component props containing pre-filled user data if available
 * @returns {JSX.Element} Account creation form
 */
const Account = (props: any) => {
    // Onboarding sub-description text
    const registerAccountSubText = (
      <span>
        Please review your First Name, Last Name and Email to ensure they have been captured correctly,
        then check the box to confirm you have read and agree to the Heidrick Assessment Privacy Policy.<br/>
        If you required assistance, please reach out to&nbsp;
        <a href="mailto:support@heidrick.com" className="supportEmail">support@heidrick.com</a>.
      </span>
    );
  const registerAccountTitle = 'Complete account creation';
  const navigate = useNavigate();
  const { globalState, dispatch } = useContext(globalContext);
  const [privacyPolicyChecked, setPrivacyPolicyCheck] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setfirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const [errors, setErrors] = useState<any>({});

  const [disableEmail, setDisableEmail] = useState(false);
  const [disableFirstName, setDisableFirstName] = useState(false);
  const [disableLastName, setDisableLastName] = useState(false);
  const [disablePrivacyPolicyChecked, setDisablePrivacyPolicyChecked] = useState(false);
  const [errorMsg, setErrorMsg] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showDownloadLoader, setShowDownloadLoader] = useState(false);
  
  // Privacy Policy constants
  const PRIVACY_POLICY_LINK_TEXT = 'Heidrick Assessment Privacy Policy';
  const PRIVACY_POLICY_ATTACHMENTID = process.env.REACT_APP_PRIVACY_POLICY_ATTACHMENTID;

  /**
   * Effect hook to populate form fields with existing data
   *
   * Initializes the form with data passed through props, typically when
   * a user has previously entered information or when the form is pre-populated
   * with data from an invitation. Also handles disabling fields that shouldn't
   * be editable based on pre-existing values.
   */
  useEffect(() => {
    if (props) {
      setfirstName(props?.firstName);
      setLastName(props?.lastName);
      setEmail(props?.email);
      setPrivacyPolicyCheck(props?.isPrivacyPolicyAccepted);
      setDisableEmail(props?.email !== 'Select' && props?.email !== '' && props?.email !== undefined);
      setDisableFirstName(props?.firstName !== 'Select' && props?.firstName !== '' && props?.firstName !== undefined);
      setDisableLastName(props?.lastName !== 'Select' && props?.lastName !== '' && props?.lastName !== undefined);
      setDisablePrivacyPolicyChecked(
        props?.isPrivacyPolicyAccepted !== 'Select' &&
          props?.isPrivacyPolicyAccepted !== '' &&
          props?.isPrivacyPolicyAccepted !== undefined &&
          props?.isPrivacyPolicyAccepted !== false
      );
    }
  }, [props]);

  /**
   * Validates and saves user account data
   *
   * This function:
   * 1. Validates all form fields using the validation schema
   * 2. Sets error messages if validation fails
   * 3. Creates a payload with user data
   * 4. Updates the global state with the new data
   * 5. Navigates to the next step in the onboarding process
   */
  const saveAccountData = () => {
    const firstNameMsg = validateFields.validate({ textField: firstName ? firstName : '' }, validationOptions)?.error?.details[0]?.message;
    const emailMsg = validateFields.validate({ email: email ? email : '' }, validationOptions)?.error?.details[0]?.message;
    const lastNameMsg = validateFields.validate({ textField: lastName ? lastName : '' }, validationOptions)?.error?.details[0]?.message;
    const privacyPolicyCheckedMsg = validateFields.validate({ privacyPolicy: privacyPolicyChecked ? privacyPolicyChecked : '' }, validationOptions)?.error?.details[0]?.message;

    if (firstNameMsg || emailMsg || lastNameMsg || privacyPolicyCheckedMsg) {
      setErrors({
        firstNameMsg: firstNameMsg,
        lastNameMsg: lastNameMsg,
        emailMsg: emailMsg,
        privacyPolicyCheckedMsg: privacyPolicyCheckedMsg,
      });
      return;
    }

    const payload = {
      firstName: firstName, 
      lastName: lastName,
      applications: {
        leaderPortal: {
          isPrivacyPolicyAccepted: privacyPolicyChecked,
        },
      },
      isProfileCompleted: ((!props?.isRoleStepVisible && !props?.isCompanyStepVisible && !props?.isPersonalStepVisible) || !props?.anyDemographicsEnabled) ? true : false, // Mark profile as completed if there are no remaining visible onboarding steps or if no demographic fields are enabled
    };

    const newContextData = { ...globalState?.leaderData, ...payload };
    const redirectUrl = (props?.anyDemographicsEnabled === false) ? '/dashboard' : ((props?.isRoleStepVisible) ? '/register-role' : (props?.isCompanyStepVisible) ? '/register-company' :  (props?.isPersonalStepVisible) ? '/register-personal' : '/dashboard');
    updateDemographicData({ payload, leaderProfileId: props.leaderProfileId, redirectUrl, setErrorMsg, newContextData, dispatch, navigate, setShowLoader });
  };

  // Privacy Policy agreement label text
  const PRIVACY_POLICY_LABEL_TEXT = 'By checking this box, you agree to our';

  /**
   * Renders the privacy policy label with text
   *
   * Creates a fragment containing the privacy policy text without the link
   * The actual link is added separately in the JSX
   *
   * @returns {JSX.Element} React fragment with privacy policy text
   */
  const policyLabel = () => {
    return <>{PRIVACY_POLICY_LABEL_TEXT}&nbsp;</>;
  };

  /**   
   GET and downloads the Heidrick Assessment Privacy Policy PDF using the attachment ID from environment variables (attachmentId might get Change from DevOps team)
   */
  const downloadPrivacyPolicyPDF = () => {
    setShowDownloadLoader(true);
    if (!PRIVACY_POLICY_ATTACHMENTID) {
      console.error('REACT_APP_PRIVACY_POLICY_ATTACHMENTID is not defined in environment variables');
      setShowDownloadLoader(false);
      return;
    }
    
    Api.downloadAssessmentprivacyPolicyPDF(PRIVACY_POLICY_ATTACHMENTID)
      .then((res: any) => {
        if (res?.success) {
          setShowDownloadLoader(false);
        } else {
          console.error('Failed to download Privacy Policy PDF');
        }
      })
      .catch((err: any) => {
        setShowDownloadLoader(false);
        console.error('Error downloading Privacy Policy PDF:', err);
      })
      .catch((err: any) => {
      setShowDownloadLoader(false);
      if (err?.code === 401 && err?.message === 'Unauthorized Access') {
        dispatch({
          type: "SET_SHOW_LOGOUT_MODAL",
          payload: true,
        });
      }
    });
  };

  return (
    <div className='accountWrapper'>
      {props?.isRoleStepVisible || props?.isCompanyStepVisible || props?.isPersonalStepVisible ? (
        <ShowStepper 
          activeIndex={props?.activeIndex} 
          isRoleStepVisible={props?.isRoleStepVisible}
          isCompanyStepVisible={props?.isCompanyStepVisible}
          isPersonalStepVisible={props?.isPersonalStepVisible}
        />
      ) : null}
      <h2 className='onboardingTitle'>{registerAccountTitle}</h2>
      <div className='onboardingSubDesc'>
        {registerAccountSubText}
      </div>
      <div className='twoFieldWrap'>
        {/* First Name Field with validation */}
        <div className='fieldWrap'>
          <TextInput
            htmlType='text'
            id='firstName'
            labelProps={{
              text: 'First Name',
              size: LabelSize.Medium,
              classNames: 'labelText',
            }}
            clearable={!disableFirstName}
            maxlength={100}
            minlength={0}
            name='firstName'
            value={firstName}
            onChange={e => {
              setfirstName(e.target.value);
            }}
            inputWidth={TextInputWidth.fill}
            classNames={disableFirstName ? 'inputTextFieldDisabled' : 'inputTextField'}
            size={TextInputSize.Large}
            status={errors?.firstNameMsg ? 'error' : 'validating'}
            disabled={disableFirstName ?? false}
            iconProps={disableFirstName ? { path: IconName.mdiLock } : undefined}
            alignIcon={disableFirstName ? TextInputIconAlign.Right : undefined}
          />
          {errors?.firstNameMsg && <span className='errorMessage'>{errors?.firstNameMsg}</span>}
        </div>

        {/* Last Name Field with validation */}
        <div className='fieldWrap'>
          <TextInput
            htmlType='text'
            id='lastName'
            clearable={!disableLastName}
            labelProps={{
              text: 'Last Name',
              size: LabelSize.Medium,
              classNames: 'labelText',
            }}
            maxlength={100}
            minlength={0}
            name='lastName'
            value={lastName}
            onChange={e => {
              setLastName(e.target.value);
            }}
            inputWidth={TextInputWidth.fill}
            classNames={disableLastName ? 'inputTextFieldDisabled' : 'inputTextField'}
            size={TextInputSize.Large}
            status={errors?.lastNameMsg ? 'error' : 'validating'}
            disabled={disableLastName ?? false}
            iconProps={disableLastName ? { path: IconName.mdiLock } : undefined}
            alignIcon={disableLastName ? TextInputIconAlign.Right : undefined}
          />
          {errors?.lastNameMsg && <span className='errorMessage'>{errors?.lastNameMsg}</span>}
        </div>
      </div>

      {/* Email Field with validation */}
      <div className='fieldWrap'>
        <TextInput
          htmlType='email'
          id='email'
          clearable={!disableEmail}
          labelProps={{
            text: 'Email',
            size: LabelSize.Medium,
            classNames: 'labelText',
          }}
          maxlength={100}
          minlength={0}
          name='email'
          value={email}
          onChange={e => {
            setEmail(e.target.value);
          }}
          inputWidth={TextInputWidth.fill}
          classNames={disableEmail ? 'inputTextFieldDisabled' : 'inputTextField'}
          size={TextInputSize.Large}
          status={errors?.emailMsg ? 'error' : 'validating'}
          disabled={disableEmail ?? false}
          iconProps={disableEmail ? { path: IconName.mdiLock } : undefined}
          alignIcon={disableEmail ? TextInputIconAlign.Right : undefined}
        />
        {errors?.emailMsg && <span className='errorMessage'>{errors?.emailMsg}</span>}
      </div>

      {/* Privacy Policy Checkbox and Link */}
      <div className='filedWrap-Link'>
        <div style={{ display: 'flex' }}>
          <CheckBox
            ariaLabel='Label'
            id='privacyPolicy'
            label={policyLabel()}
            labelAlign={LabelAlign.Center}
            labelPosition={LabelPosition.End}
            name='myCheckBoxName'
            checked={privacyPolicyChecked}
            onChange={e => {
              setPrivacyPolicyCheck(e.target.checked);
            }}
            variant={SelectorVariant.Default}
            classNames='formCheckbox'
            disabled={disablePrivacyPolicyChecked ?? false}
          />
          <Link
            classNames={`privacyPolicy ${(showDownloadLoader)  ? 'disabled-link' : ''}`}
            href='#'
            onClick={e => {
              e.preventDefault();
              if (!showDownloadLoader) {
                downloadPrivacyPolicyPDF();
              }
            }}
            target='_self'
            rel='noopener noreferrer'
            underline
            variant='primary'
          >
            {PRIVACY_POLICY_LINK_TEXT}
            {showDownloadLoader && <Icon path={IconName.mdiLoading} spin size={IconSize.Small} classNames='externalLinkIcon' />}
          </Link>
        </div>
        {errors?.privacyPolicyCheckedMsg && <span className='errorMessage'>{errors?.privacyPolicyCheckedMsg}</span>}
      </div>

      {/* Continue Button with Loading State */}
      <div className='fieldWrap-Btn-single'>
        <Button
          text='Continue'
          variant={ButtonVariant.Secondary}
          classNames='actionButton'
          style={actionButtonStyle}
          onClick={() => {
            saveAccountData();
          }}
          iconProps={showLoader ? { path: IconName.mdiLoading, spin: true } : { path: IconName.mdiArrowRight }}
          alignIcon={ButtonIconAlign.Right}
          disabled={showLoader}
        />
      </div>
      {errorMsg && <span className='apiErrorMessage'>An error occurred</span>}
    </div>
  );
};

/**
 * Export the Account component as the default export
 * This allows it to be imported using:
 * import Account from './pages/onboarding/Account';
 */
export default Account;
